import React, { useState } from "react";
import { Form, message, Row, Col, Alert } from "antd";
import { SaveButton, InputText, CancelButton, ModalComponent, trans, InputDate, SelectWithSearch, InputTextArea, UploadFile } from "@comps/components";
import { makeRequest, getErrorProps } from "@utils/helpers";
import { disburseReq } from "../requests";
import { Const } from "../../constants";
import dayjs from "dayjs";

const langKey = Const["myPayslip"].lngKey;
const formName = "disburseSalary";

const methodOptions = [
  { label: "Cash", value: "cash" },
  { label: "Bank Transfer", value: "bank" },
  { label: "Cheque", value: "cheque" },
  { label: "Online", value: "online" },
];

const DisburseSalary = (props) => {
  const record = props.record; // payslip row
  const [, setLoader] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [form] = Form.useForm();

  const netSalary = Number(record.net_salary || 0);
  const paidAmount = Number(record.paid_amount || 0);
  const remaining = Math.max(Number((netSalary - paidAmount).toFixed(2)), 0);

  const onSubmit = (data) => {
    const amount = Number(data.amount);
    if (!(amount > 0)) {
      message.error("Amount must be greater than zero");
      return;
    }
    if (amount > remaining) {
      message.error(`Amount exceeds pending (unreleased) salary (${remaining})`);
      return;
    }
    const payload = {
      disbursement: {
        payslip_id: record.id,
        amount: amount,
        method: data.method,
        paid_at: data.paid_at.format("YYYY-MM-DD"),
        reference_no: data.reference_no || null,
        remarks: data.remarks || null,
      },
      proof: fileList.length > 0 && fileList[0].id === undefined ? fileList[0].originFileObj : null,
    };
    makeRequest(setLoader, disburseReq, payload, onSuccess, onError);
    setSaving(true);
  };

  const onSuccess = (data, res) => {
    setSaving(false);
    message.success("Salary released");
    props.onCompleted(true);
  };

  const onError = (err, res) => {
    setSaving(false);
    message.error(res?.response?.data?.message || "Failed to record payment");
  };

  const footer = [
    <SaveButton loading={ saving } form={ formName } key="save_button" htmlType="submit" />,
    <CancelButton key="close_button" onClick={ () => props.onCompleted(false) } />,
  ];

  return (
    <ModalComponent top={ 20 } width={ 800 } maskClosable={ false }
      mainTitle={ trans(`${langKey}.markPaid`) } subTitle={ record.requestor?.full_name_code || "" }
      visible={ true } footer={ footer } onCancel={ () => props.onCompleted(false) }>
      <Alert type="info" showIcon style={ { marginBottom: 16 } }
        message={ `Net Salary: ${netSalary}   |   Already Released: ${paidAmount}   |   Pending: ${remaining}` } />
      <Form { ...formLayout } form={ form } name={ formName } onFinish={ onSubmit }
        initialValues={ { amount: remaining, paid_at: dayjs() } }>
        <Row gutter={ 30 }>
          <Col lg={ 12 } sm={ 24 } xs={ 24 }>
            <Form.Item name="method" rules={ rules.method } label={ trans(`${langKey}.method`) }
              className="da-mb-16" { ...getErrorProps(errors['method']) }>
              <SelectWithSearch options={ methodOptions } placeholder={ trans(`${langKey}.method`) } />
            </Form.Item>
          </Col>
          <Col lg={ 12 } sm={ 24 } xs={ 24 }>
            <Form.Item name="amount" rules={ rules.amount } label={ trans(`${langKey}.paymentAmount`) }
              className="da-mb-16" { ...getErrorProps(errors['amount']) }>
              <InputText type="number" onWheel={ (e) => e.target.blur() } placeholder={ trans(`${langKey}.paymentAmount`) } />
            </Form.Item>
          </Col>
          <Col lg={ 12 } sm={ 24 } xs={ 24 }>
            <Form.Item name="paid_at" rules={ rules.paid_at } label={ trans(`${langKey}.paidAt`) }
              className="da-mb-16" { ...getErrorProps(errors['paid_at']) }>
              <InputDate format="YYYY-MM-DD" />
            </Form.Item>
          </Col>
          <Col lg={ 12 } sm={ 24 } xs={ 24 }>
            <Form.Item name="reference_no" label={ trans(`${langKey}.referenceNo`) } className="da-mb-16">
              <InputText placeholder={ trans(`${langKey}.referenceNo`) } />
            </Form.Item>
          </Col>
          <Col lg={ 12 } sm={ 24 } xs={ 24 }>
            <Form.Item label={ trans(`${langKey}.proof`) } name="proof">
              <UploadFile fileList={ fileList } setFileList={ setFileList } accept=".pdf,.jpg,.jpeg,.png" />
            </Form.Item>
          </Col>
          <Col lg={ 12 } sm={ 24 } xs={ 24 }>
            <Form.Item name="remarks" label={ trans(`${langKey}.remarks`) } className="da-mb-16">
              <InputTextArea placeholder={ trans(`${langKey}.remarks`) } />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </ModalComponent>
  );
};

export default DisburseSalary;

const rules = {
  method: [{ required: true, message: trans(`${langKey}.method`) }],
  amount: [{ required: true, message: trans(`${langKey}.amount`) }],
  paid_at: [{ required: true, message: trans(`${langKey}.paidAt`) }],
};

const formLayout = {
  layout: "vertical",
  labelCol: { span: 6 },
  wrapperCol: { span: 24 },
  labelAlign: "left",
};
